<template>
  <div v-if="loaded" class="container is-fluid" ref="container">
    <section class="section">
      <h1 class="title">{{ $t("mp:deal.title.list") }}</h1>

      <FieldListing
        v-if="loaded"
        :data="bnplDeals"
        :columns="tableColumns"
        :actions="actions"
        :searchable="true"
        :paginated="true"
        default-sort="created"
        store-fetch-command="deal/fetchList"
        store-get-command="deal/list"
        :search-fields="searchFields"
        ref="dataTable"
      >
        <b-button
          v-if="true"
          type="is-blue"
          @click="newDeal"
          class="is-pulled-right"
        >
          {{ $t("mp:new_application.button.new_in_store_application") }}
        </b-button>
      </FieldListing>

      <LoadingSkeleton v-else />
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import i18n from "@/plugins/i18n";
import {
  BNPL_DEAL_STATUS,
  BNPL_DEAL_STATUS_RESULT_COLORS,
  BNPL_DEAL_STATUS_RESULT_TOKENS,
  BNPL_DEAL_STATUS_TOKENS,
  FLOW_POSITIONS,
  SEARCH_FIELD_TYPES,
} from "@/utils/const";
import { formatDateTime } from "@/utils/util";
import escapeHtml from "escape-html";
import LoadingSkeleton from "@/components/LoadingSkeleton";

export default {
  name: "BnplDealList",
  title: "mp:page.bnpl_deal_list.title",
  components: {
    LoadingSkeleton,
    FieldListing: () => import("@/components/FieldListing.vue"),
  },
  computed: {
    ...mapGetters({
      fileGetter: "drawer/getFile",
    }),
  },
  data() {
    return {
      bnplDeals: [],
      BNPL_DEAL_STATUS,
      BNPL_DEAL_STATUS_TOKENS,
      BNPL_DEAL_STATUS_RESULT_TOKENS,
      BNPL_DEAL_STATUS_RESULT_COLORS,
      FLOW_POSITIONS,
      searchFields: [
        {
          name: "bnpl:deal.list_header.status",
          identifier: "status",
          values: BNPL_DEAL_STATUS_TOKENS,
          type: SEARCH_FIELD_TYPES.ENUM,
          genericSearch: true,
        },
        {
          name: "bnpl:deal.list_header.created",
          identifier: "created",
          type: SEARCH_FIELD_TYPES.DATETIME,
          genericSearch: false,
        },
        {
          name: "bnpl:deal.list_header.customer_last_name",
          identifier: "customerLastName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:deal.list_header.customer_first_name",
          identifier: "customerFirstName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:deal.list_header.id",
          identifier: "id",
          type: SEARCH_FIELD_TYPES.LONG,
          genericSearch: false,
        },
        {
          name: "bnpl:deal.list_header.offer_name",
          identifier: "offerName",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
        {
          name: "bnpl:deal.list_header.current_status_result",
          identifier: "currentStatusResult",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: false,
        },
        {
          name: "bnpl:deal.list_header.order.id",
          identifier: "orderId",
          type: SEARCH_FIELD_TYPES.TEXT,
          genericSearch: true,
        },
      ],
      tableColumns: [
        {
          field: "customer.name",
          label: "bnpl:deal.list_header.name",
          sortable: false,
          format: (row) => {
            if (row.customerLastName) {
              return row.customerLastName + " " + row.customerFirstName;
            }
            return "-";
          },
        },
        {
          field: "offer.name",
          label: "bnpl:deal.list_header.offer",
          sortable: false,
          vhtml: true,
          format: (row) => {
            return `
<div style="display: flex;">
  <div style="width: 10%; margin-right: 0.5rem">
    <img src="${row.partnerLogoImageUrl}"
          alt="${i18n.t("drawer:image_loader.image.alt")}"
          style="width: 64px;"
        />
  </div>
  <div style="width: 90%">
    <p class="has-text-weight-medium mb-0">${escapeHtml(row.offerName)}</p>
    <span class="is-size-7 has-text-grey-icon">${escapeHtml(
      row.offerDescription
    )}</span>
  </div>
 </div>
`;
          },
        },
        {
          field: "status",
          label: "bnpl:deal.list_header.status",
          vhtml: true,
          format: (row) => {
            return `<p
    class="has-text-weight-medium mb-0 has-text-grey-icon"
  >
    ${escapeHtml(i18n.t(BNPL_DEAL_STATUS_TOKENS[row.status]))}
  </p>
  <span class="is-size-7 ${
    BNPL_DEAL_STATUS_RESULT_COLORS[row.currentStatusResult]
  }">
    ${escapeHtml(
      i18n.t(BNPL_DEAL_STATUS_RESULT_TOKENS[row.currentStatusResult])
    )}</span
  >`;
          },
        },
        {
          field: "created",
          label: "bnpl:deal.list_header.claim.time",
          vhtml: true,
          format: (row) => {
            return `<p
    class="mb-0 ${
      row.status === BNPL_DEAL_STATUS.CLOSED
        ? "has-text-grey-icon"
        : "has-text-weight-medium"
    }"
  >
    ${this.getDate(row.created, 1)}
  </p>
  <span class="has-text-grey-icon">
    ${this.getDate(row.created, 0)}
  </span>`;
          },
        },
        {
          field: "orderId",
          label: "mp:orderId",
          format: (row) => {
            return row.orderId;
          },
        },
      ],
      actions: [
        {
          icon: "chevron-right",
          iconPack: "fa",
          type: "is-grey",
          function: (row) => {
            this.openBnplDeal(row.id);
          },
        },
      ],
      loaded: false,
    };
  },
  async created() {
    await this.fetchList();
  },
  methods: {
    ...mapActions({
      fetchOffer: "offer/fetchOne",
      fetchCustomer: "customerStore/fetchOne",
      fetchFile: "drawer/download",
    }),
    async fetchList() {
      this.loaded = false;

      await this.$store.dispatch("deal/fetchList", "sort=created,desc");
      this.bnplDeals = this.$store.getters["deal/list"];

      for (let bnplDeal of this.bnplDeals.values) {
        bnplDeal.partnerLogoImageUrl = await this.createImageUrl(
          bnplDeal.partnerLogo
        );
      }

      this.loaded = true;
    },
    async createImageUrl(documentId) {
      try {
        if (!this.fileGetter(documentId)) {
          await this.fetchFile(documentId);
        }

        let blob = this.fileGetter(documentId);
        if (blob) {
          return window.URL.createObjectURL(blob);
        }
      } catch (ex) {
        console.error(ex);
      }
      return null;
    },
    openBnplDeal(id) {
      this.$router.push({
        name: "Deal",
        params: {
          given: id,
        },
      });
    },
    getDate(date, part) {
      // example: 2021.06.15. 13:44 where part=1 is the time
      const d = formatDateTime(date);
      return d.split(" ")[part];
    },
    newDeal() {
      this.$router.push({
        name: "NewApplication",
      });
    },
  },
};
</script>

<style scoped></style>
